/**
 * DopeOS UI System Demo - Example view using the design system
 *
 * Shows how to use DopeUI in new views.
 * Access: http://localhost:3000/?view=ui_system_demo
 *
 * Organization example for new views:
 * 1. Import DopeUI
 * 2. Use consistent components
 * 3. Layout with helpers
 */

import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import DopeUI, { TERMINAL, SPACING } from '../ui/DopeOS-UI';

// View state
const viewState = {
  hp: 75,
  maxHp: 100,
  mp: 40,
  maxMp: 60,
  stamina: 85,
  loading: 0.65,
  scrollPos: 0.3,
  activeButton: null
};

registerView('ui_system_demo', function renderUISystemDemo(
  ctx,
  time,
  width,
  height,
  params
) {
  const w = width || 640;
  const h = height || 400;
  const safe = getCanonicalSafeArea({ w, h });

  // ============================================================
  // 1. BACKGROUND (Plasma)
  // ============================================================
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.globalAlpha = 0.2;
  renderBlobBackground(ctx, time, w, h);
  ctx.restore();

  // ============================================================
  // 2. HEADER PANEL
  // ============================================================
  DopeUI.Panel(ctx, safe.left, safe.top, safe.width, 35, 'DOPEOS UI v1.0');

  // Exit action
  const backW = 148;
  const backH = 24;
  const backX = safe.right - backW - SPACING.SM;
  const backY = safe.top + 5;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'ui-demo-back', x: backX, y: backY, w: backW, h: backH,
    onClick: () => navigateTo('dopeos_home'),
  });

  // Title
  DopeUI.Header(ctx, 'ORGANIZED UI SYSTEM', safe.left + SPACING.MD, safe.top + 28, 1.3);

  // ============================================================
  // 3. GRID LAYOUT
  // ============================================================
  const grid = DopeUI.Grid(
    safe.left + SPACING.MD,
    safe.top + 50,
    2,  // 2 columns
    6,  // 6 rows
    280,  // cell width
    30,   // cell height
    12    // gap
  );

  // ============================================================
  // COLUMN 1: BARS
  // ============================================================

  // HP Bar
  const cell1 = grid.cell(0, 0);
  DopeUI.Text(ctx, 'HEALTH:', cell1.x, cell1.y - 12, 'dim', 0.9);
  DopeUI.ResourceBar(ctx, cell1.x, cell1.y, cell1.width, 24, viewState.hp, viewState.maxHp, {
    label: 'HP'
  });

  // MP Bar
  const cell2 = grid.cell(0, 1);
  DopeUI.Text(ctx, 'MANA:', cell2.x, cell2.y - 12, 'dim', 0.9);
  DopeUI.ResourceBar(ctx, cell2.x, cell2.y, cell2.width, 24, viewState.mp, viewState.maxMp, {
    label: 'MP'
  });

  // Stamina Bar
  const cell3 = grid.cell(0, 2);
  DopeUI.Text(ctx, 'STAMINA:', cell3.x, cell3.y - 12, 'dim', 0.9);
  DopeUI.ProgressBar(ctx, cell3.x, cell3.y, cell3.width, 24, viewState.stamina / 100, {
    label: 'ST'
  });

  // Loading Bar
  const cell4 = grid.cell(0, 3);
  DopeUI.Text(ctx, 'LOAD:', cell4.x, cell4.y - 12, 'dim', 0.9);
  DopeUI.ProgressBar(ctx, cell4.x, cell4.y, cell4.width, 24, viewState.loading, {
    showPercent: true
  });

  // Spinner
  const cell5 = grid.cell(0, 4);
  DopeUI.Text(ctx, 'PROCESSING...', cell5.x, cell5.y + 10, 'dim', 0.9);
  DopeUI.Spinner(ctx, cell5.x + 130, cell5.y + 10, 16, time);

  // Scrollbar
  const cell6 = grid.cell(0, 5);
  DopeUI.Text(ctx, 'SCROLL:', cell6.x, cell6.y + 10, 'dim', 0.9);
  DopeUI.Scrollbar(ctx, cell6.x + 60, cell6.y, 200, cell6.height, viewState.scrollPos, 0.25);

  // ============================================================
  // COLUMN 2: BUTTONS AND PANELS
  // ============================================================

  // Info panel
  const col2Row0 = grid.cell(1, 0);
  DopeUI.Panel(ctx, col2Row0.x, col2Row0.y - 10, col2Row0.width, 70, 'INFO');

  DopeUI.Text(ctx, 'UI System for DopeOS', col2Row0.x + SPACING.SM, col2Row0.y + 15, 'normal', 0.9);
  DopeUI.Text(ctx, '- Green only colors', col2Row0.x + SPACING.SM, col2Row0.y + 32, 'dim', 0.8);
  DopeUI.Text(ctx, '- Modular components', col2Row0.x + SPACING.SM, col2Row0.y + 46, 'dim', 0.8);
  DopeUI.Text(ctx, '- Plasma background', col2Row0.x + SPACING.SM, col2Row0.y + 60, 'dim', 0.8);

  // Buttons
  const buttons = [
    { id: 'attack', label: 'ATTACK', row: 2, state: 'normal' },
    { id: 'defend', label: 'DEFEND', row: 3, state: 'hover' },
    { id: 'item', label: 'ITEM', row: 4, state: 'pressed' },
    { id: 'flee', label: 'FLEE', row: 5, state: 'disabled' }
  ];

  buttons.forEach(btn => {
    const cell = grid.cell(1, btn.row);

    // Simulate different states for demo
    const btnState = viewState.activeButton === btn.id ? 'pressed' :
                     btn.id === 'defend' ? 'hover' :
                     btn.id === 'flee' ? 'disabled' : 'normal';

    DopeUI.Button(ctx, cell.x, cell.y, 120, 32, btn.label, btnState);

    // Demo: change state on keypress 1-4
    // In real app, this comes from inputManager
  });

  // Large button on the side
  DopeUI.Button(ctx, grid.cell(1, 2).x + 130, grid.cell(1, 2).y, 140, 74, 'EXECUTE', 'normal');

  // ============================================================
  // 4. FOOTER
  // ============================================================

  // Bottom panel
  DopeUI.Panel(ctx, safe.left, safe.bottom - 40, safe.width, 40);

  // Instructions
  DopeUI.Text(ctx,
    'KEYS: 1-4 CHANGE BARS | WASD NAVIGATE | ENTER SELECT',
    safe.left + SPACING.MD,
    safe.bottom - 22,
    'dim',
    0.9
  );

  // ============================================================
  // 5. ANIMATIONS (for demo)
  // ============================================================
  viewState.loading = 0.3 + (Math.sin(time * 2) * 0.3 + 0.3);
  viewState.stamina = 60 + Math.sin(time * 1.5) * 25;
});

// Input handling
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', (e) => {
    switch(e.key) {
      case '1': viewState.hp = Math.max(0, viewState.hp - 10); break;
      case '2': viewState.hp = Math.min(viewState.maxHp, viewState.hp + 10); break;
      case '3': viewState.mp = Math.max(0, viewState.mp - 10); break;
      case '4': viewState.mp = Math.min(viewState.maxMp, viewState.mp + 10); break;
      case 'w': viewState.scrollPos = Math.max(0, viewState.scrollPos - 0.1); break;
      case 's': viewState.scrollPos = Math.min(1, viewState.scrollPos + 0.1); break;
    }
  });
}

export default 'ui_system_demo';
